import React, { useState } from 'react';
import { NavLink, Outlet } from 'react-router-dom';
import { Header, Navigation, Content, Container } from '#components/shell';
import UserMenu from '#components/user-menu';
import Logo from '#components/logo';
import NavBar from '#components/nav-bar';
import useNavigation from '#hooks/use-navigation';
import { useMenuActions } from '#hooks/use-menu-actions';
import useAuth from '#lib/hooks/use-auth.jsx';
import { cn } from '#lib/helpers/class.js';
import Notificator from '@absolute/cloud-notify';
import '@absolute/cloud-notify/styles.css';
import { Icon } from '@iconify-icon/react';
import ManageControls from '#components/manage-controls.jsx';
import LanguageSelect from './language-select.jsx';
import useEnableUpdateFlag from '#hooks/use-enable-flight-update-flag.js';
import { NOTIFICATION_TYPES } from '#helpers/types.js';

export default function AppShell() {
    const navigation = useNavigation();
    const { isAuthenticated, user } = useAuth();

    const menuActions = useMenuActions();
    const notificatorChannel = `TripsAround_${user?.username}`;
    const { enableUpdateFlag } = useEnableUpdateFlag();
    const [lastNotifyId, setLastNotifyId] = useState(null);

    return (
        <Container>
            <ManageControls.Provider hideControls={false}>
                <Header>
                    <Navigation>
                        <Logo
                            to={isAuthenticated ? '/home' : '/signin'}
                            url={`https://${PUBLIC_BUCKET_NAME}.s3.eu-west-3.amazonaws.com/images/logo/logo.png`}
                            altText={'TripsAround'}
                        />
                        <NavBar navigation={{ routes: navigation.routes }} />

                        {isAuthenticated ? (
                            <div className="relative ml-auto flex items-center gap-2">
                                <Notificator channel={notificatorChannel}>
                                    <Notificator.Button className="relative flex items-center">
                                        {({ toRead }) => {
                                            const type = toRead[0]?.data?.type;
                                            const operationId = toRead[0]?.operationId;

                                            if (
                                                type === NOTIFICATION_TYPES.FLIGHT_UPDATE &&
                                                lastNotifyId !== operationId
                                            )
                                                enableUpdateFlag.mutate({
                                                    flightKey: toRead[0]?.data?.id,
                                                    itineraryId: toRead[0]?.data?.itineraryId
                                                });

                                            if (operationId) setLastNotifyId(() => operationId);

                                            return (
                                                <React.Fragment>
                                                    <Icon
                                                        icon="material-symbols:notifications-outline-rounded"
                                                        width="1.5rem"
                                                        height="1.5rem"
                                                        className="text-neutral-700"
                                                    />
                                                    <span
                                                        className={cn(
                                                            toRead.length > 0 ? 'opacity-100' : 'opacity-0',
                                                            toRead.length.toString().length > 1
                                                                ? 'h-6 w-6 leading-6'
                                                                : 'h-5 w-5 leading-5',
                                                            'absolute -right-1 -top-2 block rounded-full bg-gradient-to-r from-rose-600 to-rose-700 text-xs text-neutral-100 shadow transition'
                                                        )}
                                                    >
                                                        {toRead.length > 20 ? '20+' : toRead.length}
                                                    </span>
                                                </React.Fragment>
                                            );
                                        }}
                                    </Notificator.Button>
                                    <Notificator.Panel className="rounded-md border-2 border-neutral-200 bg-white z-20">
                                        <Notificator.Table />
                                    </Notificator.Panel>
                                </Notificator>
                                <LanguageSelect />
                                <UserMenu actions={menuActions} />
                            </div>
                        ) : (
                            <>
                                <NavLink
                                    className={cn(
                                        'bg-transparent text-neutral-600 hover:text-neutral-800 -mx-2 -my-1.5 inline-flex whitespace-nowrap rounded-lg px-3 py-1.5 transition-colors'
                                    )}
                                    to="/signin"
                                >
                                    {_t.auth.login()}
                                </NavLink>
                                <NavLink
                                    to="/signup"
                                    className={cn(
                                        'bg-transparent text-neutral-600 hover:text-neutral-800 -mx-2 -my-1.5 inline-flex whitespace-nowrap rounded-lg px-3 py-1.5 transition-colors'
                                    )}
                                >
                                    {_t.auth.signup()}
                                </NavLink>
                                <div className="relative ml-auto flex items-center gap-2">
                                    <LanguageSelect />
                                </div>
                            </>
                        )}
                    </Navigation>
                    <ManageControls />
                </Header>
                <Content>
                    <Outlet />
                </Content>
            </ManageControls.Provider>
        </Container>
    );
}
